import { Link } from 'react-router-dom';
import { Plus, ClipboardCheck, FileText, Clock, CheckCircle } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { useAuditPreps } from '@/hooks/use-audit-prep';
import type { AuditPrepListItem } from '@/lib/types';
import { getAuditTypeLabel } from '@/lib/audit-types';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';

const STATUS_BADGE: Record<string, { className: string; label: string }> = {
  in_progress: { className: 'bg-blue-100 text-blue-800', label: 'In Progress' },
  ready_for_review: { className: 'bg-yellow-100 text-yellow-800', label: 'Ready for Review' },
  submitted: { className: 'bg-purple-100 text-purple-800', label: 'Submitted' },
  completed: { className: 'bg-green-100 text-green-800', label: 'Completed' },
};

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_BADGE[status] ?? { className: 'bg-gray-100 text-gray-800', label: status };
  return <Badge className={config.className}>{config.label}</Badge>;
}

function ProgressBar({ item }: { item: AuditPrepListItem }) {
  const { total, answered } = item.response_summary;
  if (total === 0) return <span className="text-xs text-gray-400">No responses</span>;
  const pct = Math.round((answered / total) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-600 rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-gray-600">{answered}/{total}</span>
    </div>
  );
}

export function AuditPrepPage() {
  const timeZone = useOrgTimeZone();
  const { data: preps, isLoading } = useAuditPreps();

  const inProgressCount = preps?.filter((p) => p.status === 'in_progress').length ?? 0;
  const completedCount = preps?.filter((p) => p.status === 'completed').length ?? 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Audit Preparation
          </h1>
          <p className="text-gray-600 mt-1">
            IRATA FM-333 &amp; FM-312 audit form generator
          </p>
        </div>
        <Button asChild>
          <Link to="/compliance/audit-prep/new">
            <Plus className="h-4 w-4 mr-2" />
            New Audit Prep
          </Link>
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <ClipboardCheck className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{preps?.length ?? '-'}</p>
                <p className="text-sm text-gray-600">Total Sessions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold">{inProgressCount}</p>
                <p className="text-sm text-gray-600">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{completedCount}</p>
                <p className="text-sm text-gray-600">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stacked cards on narrow screens (≤480px) */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : !preps || preps.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-600 font-medium">No audit preparations yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Create your first audit prep to generate FM-333 and FM-312 forms
            </p>
            <Button asChild className="mt-4">
              <Link to="/compliance/audit-prep/new">
                <Plus className="h-4 w-4 mr-2" />
                New Audit Prep
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3 min-[481px]:hidden">
            {preps.map((prep) => (
              <Card key={prep.id}>
                <CardContent className="space-y-3 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Link
                        to={`/compliance/audit-prep/${prep.id}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {getAuditTypeLabel(prep.audit_type, prep.audit_type_display)}
                      </Link>
                      {prep.created_by_username && (
                        <p className="text-xs text-muted-foreground">
                          by {prep.created_by_username}
                        </p>
                      )}
                    </div>
                    <StatusBadge status={prep.status} />
                  </div>

                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                        Target Date
                      </dt>
                      <dd className="mt-0.5 text-gray-600">
                        {formatDateOnly(prep.audit_date, '-')}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                        Auditor
                      </dt>
                      <dd className="mt-0.5 text-gray-600">{prep.auditor_name || '-'}</dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                        Progress
                      </dt>
                      <dd className="mt-0.5">
                        <ProgressBar item={prep} />
                      </dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                        PDFs
                      </dt>
                      <dd className="mt-0.5">
                        <div className="flex gap-1">
                          {prep.generated_fm333_pdf && (
                            <Badge variant="outline" className="text-xs">
                              FM-333
                            </Badge>
                          )}
                          {prep.generated_fm312_pdf && (
                            <Badge variant="outline" className="text-xs">
                              FM-312
                            </Badge>
                          )}
                          {!prep.generated_fm333_pdf && !prep.generated_fm312_pdf && (
                            <span className="text-xs text-gray-400">None</span>
                          )}
                        </div>
                      </dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="hidden min-[481px]:block rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Audit Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Target Date</TableHead>
                <TableHead>Auditor</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead className="hidden md:table-cell">PDFs</TableHead>
                <TableHead className="hidden md:table-cell">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preps.map((prep) => (
                <TableRow key={prep.id} className="hover:bg-gray-50">
                  <TableCell>
                    <Link
                      to={`/compliance/audit-prep/${prep.id}`}
                      className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {getAuditTypeLabel(prep.audit_type, prep.audit_type_display)}
                    </Link>
                    {prep.created_by_username && (
                      <p className="text-xs text-gray-500">by {prep.created_by_username}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={prep.status} />
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {prep.audit_date
                      ? formatDateOnly(prep.audit_date)
                      : '-'}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {prep.auditor_name || '-'}
                  </TableCell>
                  <TableCell>
                    <ProgressBar item={prep} />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="flex gap-1">
                      {prep.generated_fm333_pdf && (
                        <Badge variant="outline" className="text-xs">FM-333</Badge>
                      )}
                      {prep.generated_fm312_pdf && (
                        <Badge variant="outline" className="text-xs">FM-312</Badge>
                      )}
                      {!prep.generated_fm333_pdf && !prep.generated_fm312_pdf && (
                        <span className="text-xs text-gray-400">None</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-gray-500">
                    {formatDateTimeInTimeZone(prep.created_at, timeZone, { dateStyle: 'medium' })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </>
      )}
    </div>
  );
}
